import { Observable } from 'rxjs'
import { normalize, schema } from 'normalizr'

import { request, parseAPIError, DownloadRequest } from '../../common/api'
import { trailerEquipmentSchema } from '../../common/normalizrSchemas'
import { normalizedEntitiesToRecordMap } from '../../common/helpers'

import * as actionTypes from './actionTypes'
import * as actionCreators from './actionCreators'
import * as tableModelActionTypes from '../../common/table/actionTypes'
import * as tableModelActionCreators from '../../common/table/actionCreators'
import { resolveModelState } from '../../common/table/helpers'
import { TrailerEquipment } from './model'
import { Trailer } from '../trailers/model'
import * as trailersActionCreators from '../trailers/actionCreators'
import { Equipment } from '../equipments/model'
import * as equipmentsActionCreators from '../equipments/actionCreators'

// Fetch

const fetchTrailerEquipmentsEpic = (action$, store) => {
    const tableIdentifier = 'trailer_equipments_list'
    const serverSide = true

    const triggeringAction = serverSide
        ? action$.ofType(actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED)
        : action$.ofType(actionTypes.FETCH)

    return triggeringAction
        .filter(action => action.type !== tableModelActionTypes.CONFIGURATION_CHANGED || action.payload.tableIdentifier === tableIdentifier)
        .switchMap(action => {
            const modelState = resolveModelState(tableIdentifier, store.getState(), action)

            const requestParams = {
                method: 'GET',
                path: `trailer-equipments/list`,
            }

            if (serverSide) {
                requestParams.method = 'POST'
                requestParams.path += `?page=${modelState.getIn(['pagination', 'current']) + 1}`
                requestParams.body = {
                    sorting: modelState.get('sorting').toJS(),
                    filters: modelState.get('filters').toJS(),
                }
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_STARTED,
                }),
                request(requestParams)
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(
                            serverSide ? ajaxResponse.response.data : ajaxResponse.response,
                            new schema.Array(trailerEquipmentSchema)
                        )

                        const trailerEquipments = normalizedEntitiesToRecordMap(
                            normalizedData.entities.trailer_equipments,
                            TrailerEquipment,
                            normalizedData.result
                        )
                        const trailers = normalizedEntitiesToRecordMap(normalizedData.entities.trailers, Trailer)
                        const equipments = normalizedEntitiesToRecordMap(normalizedData.entities.equipments, Equipment)

                        const observables = [
                            Observable.of(trailersActionCreators.fetchTrailersFulfilled(trailers)),
                            Observable.of(equipmentsActionCreators.fetchEquipmentsFulfilled(equipments)),
                            Observable.of(actionCreators.fetchTrailerEquipmentsFulfilled(trailerEquipments)),
                        ]

                        serverSide &&
                            observables.push(
                                Observable.of(
                                    tableModelActionCreators.updatePagination(
                                        tableIdentifier,
                                        ajaxResponse.response.last_page,
                                        ajaxResponse.response.current_page - 1,
                                        ajaxResponse.response.total
                                    )
                                )
                            )

                        return Observable.concat(...observables)
                    })
                    .catch(error => Observable.of(actionCreators.fetchTrailerEquipmentsRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_CANCELLED, actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED))
            )
        })
}

// Fetch one

const fetchTrailerEquipmentEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_ONE)
        .filter(() => !store.getState().trailerEquipments.getIn(['current', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_ONE_STARTED,
                }),
                request({
                    path: `trailer-equipments/${action.payload}`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, trailerEquipmentSchema)

                        const trailerEquipments = normalizedEntitiesToRecordMap(normalizedData.entities.trailer_equipments, TrailerEquipment)

                        return Observable.concat(
                            Observable.of(actionCreators.fetchTrailerEquipmentsFulfilled(trailerEquipments)),
                            Observable.of(actionCreators.fetchTrailerEquipmentFulfilled())
                        )
                    })
                    .catch(error => Observable.of(actionCreators.fetchTrailerEquipmentsRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_ONE_CANCELLED))
            )
        )

// Save

const saveTrailerEquipmentEpic = (action$, store) =>
    action$
        .ofType(actionTypes.SAVE)
        .filter(() => !store.getState().trailerEquipments.getIn(['current', 'saving']))
        .switchMap(action => {
            const values = Object.assign({}, action.payload.values)
            if (action.payload.ignoreDuplicity) {
                values.ignore_duplicity = 1
            }

            let path = `trailer-equipments`
            let method = 'POST'

            if (values.id) {
                path = `trailer-equipments/${values.id}`
                method = 'PUT'

                delete values.id
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.SAVE_STARTED,
                }),
                request({
                    path,
                    method,
                    body: values,
                })
                    .switchMap(ajaxResponse => {
                        if (ajaxResponse.response.duplicity_found) {
                            return Observable.concat(
                                Observable.of(actionCreators.saveTrailerEquipmentDuplicityFound(ajaxResponse.response.duplicity))
                            )
                        }

                        const normalizedData = normalize(ajaxResponse.response, trailerEquipmentSchema)

                        const trailerEquipments = normalizedEntitiesToRecordMap(normalizedData.entities.trailer_equipments, TrailerEquipment)
                        const trailerEquipment = trailerEquipments.valueSeq().first()

                        return Observable.of(actionCreators.saveTrailerEquipmentFulfilled(trailerEquipment))
                    })
                    .catch(error => Observable.of(actionCreators.saveTrailerEquipmentRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.SAVE_CANCELLED))
            )
        })

// Delete

const deleteTrailerEquipmentEpic = (action$, store) =>
    action$
        .ofType(actionTypes.DELETE)
        .filter(() => !store.getState().trailerEquipments.getIn(['deletion', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.DELETE_STARTED,
                }),
                request({
                    path: `trailer-equipments/${action.payload}`,
                    method: 'DELETE',
                })
                    .switchMap(() => Observable.of(actionCreators.deleteTrailerEquipmentFulfilled(action.payload)))
                    .catch(error => Observable.of(actionCreators.deleteTrailerEquipmentRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.DELETE_CANCELLED))
            )
        )

// Export

const exportTrailerEquipmentsEpic = (action$, store) =>
    action$.ofType(actionTypes.EXPORT).switchMap(action => {
        const filters = JSON.stringify(action.payload.filters)
        const sorting = JSON.stringify(action.payload.sorting)
        const token = store.getState().auth.get('accessToken')

        new DownloadRequest({
            url: `trailer-equipments/export?filters=${filters}&sorting=${sorting}&token=${token}`,
        }).run()

        return Observable.concat(
            Observable.of({
                type: actionTypes.EXPORT_FULFILLED,
            })
        )
    })

export default [
    fetchTrailerEquipmentsEpic,
    fetchTrailerEquipmentEpic,
    saveTrailerEquipmentEpic,
    deleteTrailerEquipmentEpic,
    exportTrailerEquipmentsEpic,
]
